import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MupLayout, StateBadge } from "@/pages/mup/MupLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { FilePlus2, Search, Trash2, Copy, BookmarkPlus, FolderOpen, Play } from "lucide-react";

const STATES = [
  { v: "all", l: "Tutti gli stati" },
  { v: "bozza", l: "Bozza" },
  { v: "pronto_invio", l: "Pronto per invio" },
  { v: "inviato", l: "Inviato" },
  { v: "firmato", l: "Firmato" },
  { v: "annullato", l: "Annullato" },
];

export default function MupPratiche() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [state, setState] = useState("all");
  const [bancaEtica, setBancaEtica] = useState(false);
  const [models, setModels] = useState([]);

  const loadModels = () => api.get("/mup/modelli").then(({ data }) => setModels(data)).catch(() => setModels([]));

  const load = () => {
    const params = {};
    if (query) params.query = query;
    if (state !== "all") params.state = state;
    if (bancaEtica) params.banca_etica = true;
    api.get("/mup/pratiche", { params }).then(({ data }) => setItems(data)).catch(() => setItems([]));
  };

  useEffect(() => { load(); loadModels(); }, [state, bancaEtica]); // eslint-disable-line

  const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("it-IT"); } catch { return iso; } };

  const del = async (p, e) => {
    e.stopPropagation();
    if (!window.confirm(`Eliminare definitivamente la pratica ${p.code}${p.client_data?.name ? ` di ${p.client_data.name}` : ""}? L'operazione è irreversibile.`)) return;
    try {
      await api.delete(`/mup/pratiche/${p.pratica_id}`);
      toast.success("Pratica eliminata");
      setItems((prev) => prev.filter((x) => x.pratica_id !== p.pratica_id));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const duplica = async (p, e) => {
    e.stopPropagation();
    try {
      const { data } = await api.post(`/mup/pratiche/${p.pratica_id}/duplica`);
      toast.success(`Duplicata in bozza ${data.code}`);
      navigate(`/mup/pratiche/${data.pratica_id}/modifica`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const salvaModello = async (p, e) => {
    e.stopPropagation();
    const nome = window.prompt("Nome del modello (per ritrovarlo nel box Modelli):", p.product_name || p.client_data?.name || "");
    if (nome === null || !nome.trim()) return;
    try {
      await api.post(`/mup/pratiche/${p.pratica_id}/salva-modello`, { nome: nome.trim() });
      toast.success(`Modello «${nome.trim()}» salvato`);
      loadModels();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const usaModello = async (m) => {
    try {
      const { data } = await api.post(`/mup/modelli/${m.pratica_id}/usa`);
      toast.success(`Nuova bozza ${data.code} creata dal modello`);
      navigate(`/mup/pratiche/${data.pratica_id}/modifica`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const delModello = async (m) => {
    if (!window.confirm(`Eliminare il modello «${m.modello_nome}»?`)) return;
    try {
      await api.delete(`/mup/modelli/${m.pratica_id}`);
      toast.success("Modello eliminato");
      setModels((prev) => prev.filter((x) => x.pratica_id !== m.pratica_id));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <MupLayout
      subtitle="Archivio"
      title="Pratiche"
      actions={
        <Button data-testid="mup-new-pratica-btn" onClick={() => navigate("/mup/nuova")} className="brand-gradient text-white border-0">
          <FilePlus2 className="h-4 w-4 mr-2" /> Nuova pratica
        </Button>
      }
    >
      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            data-testid="mup-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Cerca per cliente, CF/P.IVA, codice o proposta…"
            className="pl-9 bg-white border-slate-300"
          />
        </div>
        <Select value={state} onValueChange={setState}>
          <SelectTrigger data-testid="mup-state-filter" className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            {STATES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button data-testid="mup-search-btn" variant="outline" onClick={load}>Cerca</Button>
        <Button
          data-testid="mup-banca-etica-filter"
          variant={bancaEtica ? "default" : "outline"}
          onClick={() => setBancaEtica((v) => !v)}
          className={bancaEtica ? "bg-emerald-600 hover:bg-emerald-700 text-white border-0" : ""}
        >
          Segnalati da Banca Etica
        </Button>
      </div>

      {models.length > 0 && (
        <div className="rounded-xl border border-teal-200 bg-teal-50/60 p-4 mb-4" data-testid="mup-modelli-box">
          <div className="flex items-center gap-2 mb-3 text-teal-800">
            <FolderOpen className="h-4 w-4" />
            <h3 className="font-semibold text-sm">I miei Modelli riutilizzabili</h3>
            <span className="text-xs text-teal-600">({models.length}) — visibili solo a te</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {models.map((m) => (
              <div key={m.pratica_id} className="rounded-lg border border-teal-200 bg-white p-3 flex items-center justify-between gap-2" data-testid={`mup-modello-${m.pratica_id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{m.modello_nome}</div>
                  <div className="text-xs text-slate-500 truncate">{m.product_name || "—"}{m.client_data?.name ? ` · ${m.client_data.name}` : ""}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" className="h-8 bg-teal-600 hover:bg-teal-700 text-white" onClick={() => usaModello(m)} data-testid={`mup-modello-usa-${m.pratica_id}`}>
                    <Play className="h-3.5 w-3.5 mr-1" /> Usa
                  </Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500 hover:bg-red-50" onClick={() => delModello(m)} data-testid={`mup-modello-del-${m.pratica_id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Codice</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead>Prodotto</TableHead>
              <TableHead>Proposta</TableHead>
              <TableHead>Stato</TableHead>
              <TableHead>Aggiornata</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10">Nessuna pratica trovata.</TableCell></TableRow>
            ) : items.map((p) => (
              <TableRow
                key={p.pratica_id}
                data-testid={`mup-pratica-row-${p.pratica_id}`}
                className="cursor-pointer"
                onClick={() => navigate(`/mup/pratiche/${p.pratica_id}`)}
              >
                <TableCell className="font-mono text-xs">{p.code}</TableCell>
                <TableCell>
                  <div className="text-sm font-medium text-slate-900 flex items-center gap-2">
                    {p.client_data?.name || "—"}
                    {p.banca_etica_segnalazione && (
                      <span data-testid={`mup-banca-etica-badge-${p.pratica_id}`} className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-[10px] font-medium">
                        Banca Etica
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500">{p.client_data?.fiscal_code || p.client_data?.vat || ""}</div>
                </TableCell>
                <TableCell className="text-sm text-slate-600">{p.product_name || ""}</TableCell>
                <TableCell className="text-sm text-slate-600">{p.proposal_number || "—"}</TableCell>
                <TableCell><StateBadge state={p.state} /></TableCell>
                <TableCell className="text-sm text-slate-500">{fmt(p.updated_at)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {p.state !== "firmato" && (
                      <>
                        <Button data-testid={`mup-duplica-${p.pratica_id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-teal-700 hover:bg-teal-50" title="Duplica in nuova bozza" onClick={(e) => duplica(p, e)}>
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button data-testid={`mup-salva-modello-${p.pratica_id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-teal-700 hover:bg-teal-50" title="Salva come Modello riutilizzabile" onClick={(e) => salvaModello(p, e)}>
                          <BookmarkPlus className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                    {isAdmin && (
                      <Button data-testid={`mup-del-pratica-${p.pratica_id}`} size="icon" variant="ghost" className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50" title="Elimina" onClick={(e) => del(p, e)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </MupLayout>
  );
}
